import React, { useState } from "react"
import { Box, Button } from "src/UILibrary"
import { useTranslation } from "react-i18next"
import { validateFields } from "src/modules/validation"
import * as yup from "yup"
import { OutlinedButton } from "src/components/buttons/OutlinedButton"
import InputNameField from "src/components/atom/InputNameField"
import SelectField from "src/components/atom/SelectField"
import { useLoginContext } from "src/hooks/useLoginContext"
import { Admin, ProfileService } from "src/openapi"
import { useRecoilState } from "recoil"
import { ShowToast } from "src/states/showToast"
import { MEMBER_STATUS, RESPONSE_CODE, SNACKBAR_TYPES } from "src/modules/const"
import CheckBoxGroupField from "src/components/atom/CheckBoxGroupField"
import { ConfirmModal } from "src/UILibrary/ConfirmModal"
import { ShowProgress } from "src/states/showProgress"

type Props = {
  item: Admin
}

export const AdministratorDetail = ({ item }: Props) => {
  const { t } = useTranslation("translation", { keyPrefix: "mypage.user" })

  const [, setShowToast] = useRecoilState(ShowToast)
  const [, setShowProgress] = useRecoilState(ShowProgress)

  const { areas, awsID, accessToken, checkLoginStatus } = useLoginContext()

  const [firstName, setFirstName] = useState<string>(item.firstName as string)
  const [lastName, setLastName] = useState<string>(item.lastName as string)
  const [firstNameJP, setFirstNameJP] = useState<string>(item.firstNameKana as string)
  const [lastNameJP, setLastNameJP] = useState<string>(item.lastNameKana as string)
  const [areaID, setAreaID] = useState<number>(item.areaId as number)
  const [nameValidationMessage, setNameValidationMessage] = useState<string>("")
  const [accept, setAccept] = useState<string>(item.status as string)
  const [acceptValidationMessage, setAcceptValidationMessage] = useState<string>("")

  const [showConfirm, setShowConfirm] = useState<boolean>(false)

  const validate = async () => {
    let validation = true

    try {
      await validateFields(
        yup.object().shape({
          firstName: yup.string().required(t("please_input_first_name")),
          lastName: yup.string().required(t("please_input_last_name")),
          firstNameJP: yup
            .string()
            .required(t("please_input_first_name_jp"))
            .matches(/^[ァ-ヶｱ-ﾝﾞﾟ一-]*$/, t("please_input_jp_string")),
          lastNameJP: yup
            .string()
            .required(t("please_input_last_name_jp"))
            .matches(/^[ァ-ヶｱ-ﾝﾞﾟ一-]*$/, t("please_input_jp_string")),
        }),
        ["firstName", "lastName", "firstNameJP", "lastNameJP"],
        [firstName, lastName, firstNameJP, lastNameJP]
      )
      validation = validation && true
      setNameValidationMessage("")
    } catch (err: any) {
      setNameValidationMessage(err.message)
      validation = validation && false
    }

    if (accept === undefined) {
      setAcceptValidationMessage(t("please_select_status"))
      validation = validation && false
    } else {
      setAcceptValidationMessage("")
    }

    return validation
  }

  const handleUpdate = async () => {
    if (!(await validate())) {
      return
    }

    setShowProgress(true)
    ProfileService.updateAdmin(accessToken, item.userId as string, {
      firstName: firstName,
      lastName: lastName,
      firstNameKana: firstNameJP,
      lastNameKana: lastNameJP,
      areaId: areaID,
      status: accept,
      comment: "ADMIN_UPDATE",
    })
      .then((res) => {
        setShowProgress(false)
        if (!checkLoginStatus(res)) {
          return
        }

        if (res.status === RESPONSE_CODE.SUCCESS) {
          setShowToast({ show: true, text: t("success_update"), type: SNACKBAR_TYPES.SUCCESS })
        } else {
          setShowToast({ show: true, text: t("failed_update"), type: SNACKBAR_TYPES.ERROR })
        }
      })
      .catch(() => {
        setShowProgress(false)
        setShowToast({ show: true, text: t("failed_update"), type: SNACKBAR_TYPES.ERROR })
      })
  }

  const handleRemove = () => {
    if (item.userId === awsID) {
      setShowToast({ show: true, text: t("cannot_remove_self"), type: SNACKBAR_TYPES.WARNING })
      return
    }

    setShowConfirm(true)
  }

  const handleRemoveConfirm = () => {
    setShowProgress(true)
    ProfileService.deleteAdmin(accessToken, item.userId as string)
      .then((res) => {
        setShowProgress(false)
        if (!checkLoginStatus(res)) {
          return
        }

        if (res.status === RESPONSE_CODE.SUCCESS) {
          setShowToast({ show: true, text: t("success_remove"), type: SNACKBAR_TYPES.SUCCESS })
          location.reload()
        } else {
          setShowToast({ show: true, text: t("failed_remove"), type: SNACKBAR_TYPES.ERROR })
        }
      })
      .catch(() => {
        setShowProgress(false)
        setShowToast({ show: true, text: t("failed_remove"), type: SNACKBAR_TYPES.ERROR })
      })
  }

  return (
    <Box sx={{ py: "40px", px: "20px" }}>
      <InputNameField
        name={t("name")}
        required
        firstName={firstName}
        lastName={lastName}
        firstNameJP={firstNameJP}
        lastNameJP={lastNameJP}
        handleFirstName={setFirstName}
        handleLastName={setLastName}
        handleFirstNameJP={setFirstNameJP}
        handleLastNameJP={setLastNameJP}
        validationMessage={nameValidationMessage}
      ></InputNameField>
      <SelectField
        name={t("address")}
        required
        sx={{ mt: "30px", mb: "40px", width: "200px" }}
        selected={areaID}
        handleSelected={setAreaID}
        datas={areas}
      ></SelectField>
      <CheckBoxGroupField
        required
        radio
        name={t("member_status")}
        datas={[
          { label: t("requested"), value: MEMBER_STATUS.REQUEST },
          { label: t("accepted"), value: MEMBER_STATUS.ACCEPTED },
        ]}
        selected={accept}
        handleSelected={setAccept}
        sx={{ mt: "30px" }}
        validationMessage={acceptValidationMessage}
      />
      <Box display="flex" sx={{ mt: "30px" }}>
        <OutlinedButton
          caption={t("delete")}
          fontColor="#FC3317"
          fontWeight="700"
          fontSize={16}
          sx={{
            borderColor: "#FC3317",
            minWidth: "240px",
            py: "16px",
            fontSize: "1rem",
            fontFamily: "Noto Sans JP !important",
            lineHeight: "1rem",
            fontWeight: 700,
            ml: "auto",
            mr: "40px",
            display: "flex",
            justifyContent: "center",
            borderRadius: 0,
          }}
          onClick={() => handleRemove()}
        />
        <Button
          variant="contained"
          sx={{
            minWidth: "240px",
            py: "16px",
            fontSize: "1rem",
            fontFamily: "Noto Sans JP !important",
            lineHeight: "1rem",
            fontWeight: 700,
            ml: 0,
            mr: "auto",
            display: "flex",
            justifyContent: "center",
            borderRadius: 0,
            ":hover": {
              bgcolor: "#2DA389",
            },
          }}
          onClick={() => handleUpdate()}
        >
          {t("update")}
        </Button>
      </Box>
      <ConfirmModal
        open={showConfirm}
        handleOpen={setShowConfirm}
        text={t("do_you_want_to_remove")}
        confirmText={t("remove")}
        cancelText={t("cancel")}
        handleConfirm={handleRemoveConfirm}
        confirmStyle={{ color: "#FC3317", borderColor: "#FC3317" }}
      />
    </Box>
  )
}
